import { HttpClient, HttpContext, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';

import { decodeClaims, NinetyAccessTokenClaims, ResourceTokens } from '@ninety/mobile/platform/auth';
import { ENVIRONMENT } from '@ninety/mobile/platform/environments';

import { AuthFailureError } from './auth-failure';
import { SKIP_SESSION } from './session-http.context';

/** Everything a Token Exchange returns. The Entitlements Token is fetched separately, with these. */
export type ExchangedTokens = Omit<ResourceTokens, 'entitlementsToken'>;

/**
 * The Resource Server's shape — the part of it this reads. Every field is optional on the wire.
 *
 * `ninetyUserId` is deliberately absent. The server does send one, but only on the nested
 * `responses.access.tokens` / `responses.refresh.tokens` copies, never on the top-level `tokens`.
 * Reading it from here is what broke every sign-in on the cutover. `xsrf` is also present and
 * ignored: nothing attaches an xsrf header, and the cookie is written by the server.
 */
interface TokenExchangeResponse {
  readonly tokens?: {
    readonly access?: string;
    readonly refresh?: string;
  };
}

/**
 * The Token Exchange: a credential the Authorization Server issued, traded for ones the Resource
 * Server accepts.
 *
 * Nothing here is stored and nothing is cached. The tokens go to the caller, which hands them to
 * the machine, which puts them on `authorized` — the only place Resource Tokens exist.
 */
@Injectable({ providedIn: 'root' })
export class TokenExchangeService {
  private readonly http = inject(HttpClient);
  private readonly environment = inject(ENVIRONMENT);

  /**
   * Trade an ID Token for Resource Tokens.
   *
   * Only for a session that has none yet: sign-in, and a cold start. It cannot say which company
   * user it wants — an ID Token names a person — so the Resource Server picks, and every caller
   * that already knows the answer uses `forCompanyUser` instead.
   */
  exchange(idToken: string): Observable<ExchangedTokens> {
    return this.tokens(
      this.http.post<TokenExchangeResponse>(
        `${this.environment.origin.monolith}/Login`,
        { idToken },
        { context: new HttpContext().set(SKIP_SESSION, true) }
      )
    );
  }

  /**
   * Trade an Azure authorization code for Resource Tokens.
   *
   * The same endpoint and the same answer as {@link exchange}, reached with a different request
   * because Microsoft is not federated into the Cognito user pool: there is no ID Token to send, so
   * the Resource Server is handed the code and redeems it at Azure itself, with a client secret this
   * app does not have and should not.
   *
   * **This is the last Microsoft-shaped thing in the app.** What comes back is parsed by the same
   * `tokens()` as every other Grant, so nothing below here branches on where the session came from.
   *
   * `loginWithMicrosoft` is what selects that path server-side; without it the Resource Server reads
   * the request as a Cognito one and looks for an ID Token that is not there.
   *
   * The code is deliberately **not** accompanied by a `codeVerifier`, and cannot usefully be: the
   * `Login` contract has no field for one and the Azure call it makes sends none, so a PKCE
   * challenge on the authorization request would make this redemption fail.
   */
  exchangeMicrosoftCode(code: string): Observable<ExchangedTokens> {
    return this.tokens(
      this.http.post<TokenExchangeResponse>(
        `${this.environment.origin.monolith}/Login`,
        { microsoftCode: code, loginWithMicrosoft: true },
        { context: new HttpContext().set(SKIP_SESSION, true) }
      )
    );
  }

  /**
   * Mint Resource Tokens for a named company user of the same person.
   *
   * The only call that can name one, and so the path every refresh with a live session takes —
   * whether it is preserving the current company user or a Company Switch replacing them. Cognito
   * authenticates a person and has no idea companies exist, so nothing involving an ID Token can
   * carry the answer; the Resource Refresh Token can.
   *
   * `withCredentials` is inert on native — `CapacitorHttpService` never reads it — so it only
   * affects the web build, where it sends cookies with this call. Whether the Resource Server still
   * requires the xsrf cookie is an open question in the spec, and dropping it is a change to make
   * deliberately with a device test, not in passing.
   */
  forCompanyUser(companyUserId: string, resourceRefreshToken: string): Observable<ExchangedTokens> {
    return this.tokens(
      this.http.get<TokenExchangeResponse>(`${this.environment.origin.monolith}/Refresh`, {
        withCredentials: true,
        headers: new HttpHeaders({ 'ninety-refresh-token': resourceRefreshToken }),
        params: new HttpParams().set('userId', companyUserId),
        context: new HttpContext().set(SKIP_SESSION, true),
      })
    );
  }

  private tokens(response: Observable<TokenExchangeResponse>): Observable<ExchangedTokens> {
    return response.pipe(
      map(({ tokens }) => {
        if (!tokens?.access || !tokens.refresh) {
          // Half a session is not a session, so this fails the whole exchange. It is classified as
          // retryable rather than as a rejection on purpose: the Resource Server never said no, and
          // a rejection is the answer that costs someone their Durable Root.
          throw new AuthFailureError(
            { kind: 'malformedResponse', detail: 'incompleteSession' },
            'the Token Exchange returned an incomplete session'
          );
        }

        const ninetyUserId = decodeClaims<NinetyAccessTokenClaims>(tokens.access)?.userId;

        if (!ninetyUserId) {
          throw new AuthFailureError(
            { kind: 'malformedResponse', detail: 'unreadableCompanyUser' },
            'the Access Token carried no readable company user'
          );
        }

        return { accessToken: tokens.access, refreshToken: tokens.refresh, ninetyUserId };
      })
    );
  }
}
